import * as types from "../mutation-types";
import isEqual from "lodash.isequal";

const settingGetters = {
    rebalancing: "rebalancing/rebalanceSetting",
    fee: "fees/feeSetting"
};

function initialState() {
    return {
        hypoType: 0,
        rawPortfolio: {},
        preferences: {
            languageId: "en-US",
            adminConfig: { applyStartDateType: 0 }
        },
        currencyList: [],
        currency: "USD",
        allocateBy: 0, //allocateBy: 0 - Amount, 1 - Percentage, 2 - Shares
        totalInvestAmount: 10000,
        assetAllocation: [],
        isApplyEarliestAvailable: true
    };
}
const state = {
    ...initialState()
};

const mutations = {
    [types.RESET](state) {
        const initialData = initialState();
        Object.keys(initialData).forEach(key => {
            state[key] = initialData[key];
        });
    },
    [types.SET_RAW_PORTFOLIO](state, data) {
        state.rawPortfolio = data;
    },
    [types.SET_PREFERENCES](state, data) {
        state.preferences = data.preferences;
        if (data.type === 2) {
            state.preferences.adminConfig.applyStartDateType = 1;
        }
    },
    [types.SET_ASSET_ALLOCATION](state, data) {
        state.assetAllocation = data;
    },
    [types.SET_IS_APPLY_EARLIEST_AVAILABLE](state, data) {
        state.isApplyEarliestAvailable = data;
    },
    [types.SET_CURRENCY_LIST](state, data) {
        state.currencyList = data;
    },
    [types.SET_CURRENCY](state, data) {
        state.currency = data;
    },
    [types.SET_ALLOCATE_BY](state, data) {
        state.allocateBy = data;
    },
    [types.SET_INITIAL_INVESTMENT_AMOUNT](state, data) {
        state.totalInvestAmount = data;
    },
    [types.SET_HYPO_TYPE](state, data) {
        state.hypoType = data;
    }
};

const applyFeeSetting = (securities, feeSetting, defaultFeeSetting) => {
    const { includeAdvisoryFee, includeTransactionFee } = { ...feeSetting };
    if (!includeAdvisoryFee) {
        feeSetting = {
            ...feeSetting,
            includeAdvisoryFee: false,
            annualFeeAmount: defaultFeeSetting.annualFeeAmount,
            annualFeeChargeFrequency: defaultFeeSetting.annualFeeChargeFrequency,
            annualFeePayMethod: defaultFeeSetting.annualFeePayMethod,
            annualFeePayOnBegin: defaultFeeSetting.annualFeePayOnBegin,
            annualFeePayCashFirst: defaultFeeSetting.annualFeePayCashFirst
        };
    }
    const hasSecurityTransactionFees = securities.some(security => typeof security.fees !== "undefined" && typeof security.fees.frontLoadType !== "undefined");
    if (!includeTransactionFee || !hasSecurityTransactionFees) {
        feeSetting = {
            ...feeSetting,
            includeTransactionFee,
            frontLoadType: defaultFeeSetting.frontLoadType,
            loi: defaultFeeSetting.loi,
            redemptionCharge: defaultFeeSetting.redemptionCharge,
            chargeOnRebalancing: defaultFeeSetting.chargeOnRebalancing
        };
    }
    return feeSetting;
};

const initialSecurityFees = (securities, feeSetting) => {
    const { includeAdvisoryFee, includeTransactionFee } = feeSetting;
    return securities.map((security, index, securities) => {
        let securityFees = security.fees || securities[0].fees;
        securityFees = {
            annualFeeAmountType: 1, //UI only support percentage now.
            annualFeeAmount: feeSetting.annualFeeAmount,
            annualFeeChargeFrequency: feeSetting.annualFeeChargeFrequency,
            ...(securityFees || {})
        };
        if (includeTransactionFee && (!securityFees || typeof securityFees.frontLoadType === "undefined")) {
            securityFees = {
                ...(securityFees || {}),
                frontLoadType: feeSetting.frontLoadType,
                loi: feeSetting.loi,
                redemptionCharge: feeSetting.redemptionCharge
            };
            if (feeSetting.frontLoadType === 1) {
                securityFees = {
                    ...securityFees,
                    frontLoadAmountType: feeSetting.frontLoadAmountType,
                    frontLoadAmount: feeSetting.frontLoadAmount
                };
            }
        }
        if (includeAdvisoryFee || includeTransactionFee) {
            return {
                ...security,
                fees: securityFees
            };
        }
        return security;
    });
};

const actions = {
    initSettings({ commit, rootGetters }, portfolioData) {
        let { securities, rebalance: rebalanceSetting, reinvestSetting, subSequentSetting, feeSetting, taxSetting, benchmark } = portfolioData;
        const defaultRebalanceSetting = rootGetters["rebalancing/getPreferenceRebalancingSetting"];
        const defaultReinvestSetting = rootGetters["reinvestment/getPreferenceReinvestmentSetting"];
        const defaultSubSequentSetting = rootGetters["transaction/getPreferenceSubSequentSetting"];
        const defaultFeeSetting = rootGetters["fees/getPreferenceFeeSetting"];
        const defaultTaxSetting = rootGetters["taxes/getPreferenceTaxSetting"];

        if (!(rebalanceSetting && rebalanceSetting.includeRebalance)) {
            rebalanceSetting = {
                ...defaultRebalanceSetting,
                includeRebalance: false
            };
        }

        if (!(reinvestSetting && reinvestSetting.include)) {
            reinvestSetting = {
                ...defaultReinvestSetting,
                include: false
            };
        }

        if (!(subSequentSetting && subSequentSetting.include)) {
            subSequentSetting = {
                include: false,
                liquidateOnEndDate: defaultSubSequentSetting.liquidateOnEndDate
            };
        }

        feeSetting = applyFeeSetting(securities, feeSetting, defaultFeeSetting);
        const securitiesWithFeeData = initialSecurityFees(securities, feeSetting);

        if (!(taxSetting && taxSetting.includeTax)) {
            taxSetting = {
                ...defaultTaxSetting,
                includeTax: false
            };
        }

        commit(types.SET_RAW_PORTFOLIO, portfolioData);
        commit(types.SET_CURRENCY, portfolioData.currency);
        commit(types.SET_ALLOCATE_BY, portfolioData.allocateBy);
        commit(types.SET_INITIAL_INVESTMENT_AMOUNT, portfolioData.totalInvestAmount);
        commit(`holdings/${types.SET_SECURITIES}`, securitiesWithFeeData, { root: true });
        commit(`rebalancing/${types.SET_REBALANCE_SETTING}`, rebalanceSetting, { root: true });
        commit(`reinvestment/${types.SET_REINVESTMENT_SETTING}`, reinvestSetting, { root: true });
        commit(`transaction/${types.SET_SUB_SEQUENT_SETTING}`, subSequentSetting, { root: true });
        commit(`fees/${types.UPDATE_FEE_SETTING}`, feeSetting, { root: true });
        commit(`taxes/${types.UPDATE_TAX_SETTING}`, taxSetting, { root: true });
        commit(`benchmark/${types.UPDATE_BENCHMARK_SETTING}`, benchmark, { root: true });
    },
    clearSettings({ commit }) {
        commit(types.RESET);
        commit(`fees/${types.RESET}`, undefined, { root: true });
        commit(`taxes/${types.RESET}`, undefined, { root: true });
        commit(`holdings/${types.RESET}`, undefined, { root: true });
        commit(`reinvestment/${types.RESET}`, undefined, { root: true });
        commit(`transaction/${types.RESET}`, undefined, { root: true });
        commit(`benchmark/${types.RESET}`, undefined, { root: true });
        commit(`rebalancing/${types.RESET}`, undefined, { root: true });
    },
    setHypoType({ commit }, data) {
        commit(types.SET_HYPO_TYPE, data);
    }
};

const getters = {
    getHypoType(state) {
        return state.hypoType;
    },
    isSimpleAssumptionWorkflow(state) {
        const { adminConfig } = state.preferences;
        return adminConfig && adminConfig.assumptionWorkflow === 1;
    },
    hasCustomBenchmark(state) {
        const { customBenchmark } = state.preferences.adminConfig.dataSource;
        return customBenchmark;
    },
    // portfolio level currency is for portfolio settings. In holdings page, there is currency dropdown, it allows user to enter accounts in different currencies.
    portfolioCurrencySymbol(state) {
        const selectedCurrency = state.currencyList.find(item => item.currencyId === state.currency);
        return selectedCurrency && selectedCurrency.currencySymbol ? selectedCurrency.currencySymbol : "";
    },
    portfolioSecurities(state, getters, rootState, rootGetters) {
        let securities = rootState.holdings.securities;
        const rebalanceSetting = rootGetters[settingGetters.rebalancing];
        const includeRebalance = rebalanceSetting && rebalanceSetting.includeRebalance;
        const rebalanceType = rebalanceSetting && rebalanceSetting.rebalancingTo;
        if (includeRebalance && rebalanceType === 1) {
            const mapping = {};
            rootGetters["rebalancing/getRebalanceSecurities"].forEach(item => {
                mapping[item.securityId] = item.customWeight;
            });
            //include rebalance and rebalance to is custom.
            securities = securities.map(securityInfo => {
                return {
                    ...securityInfo,
                    rebalancePercent: mapping[securityInfo.securityId]
                };
            });
        }
        const feeSetting = rootGetters[settingGetters.fee];
        const { includeAdvisoryFee, includeTransactionFee } = feeSetting;
        if (!includeAdvisoryFee && !includeTransactionFee) {
            securities = securities.map(security => {
                // eslint-disable-next-line no-unused-vars
                const { fees, ...newSecurity } = security;
                return newSecurity;
            });
        }
        return securities;
    },
    portfolioData(state, getters, rootState, rootGetters) {
        let portfolioData = {
            id: state.rawPortfolio.id, //unique id
            portfolioId: state.rawPortfolio.portfolioId,
            name: state.rawPortfolio.name,
            currency: state.currency, //*
            allocateBy: state.allocateBy, //*
            pctAllocateBy: state.rawPortfolio.pctAllocateBy,
            priceType: state.rawPortfolio.priceType,
            totalInvestAmount: state.totalInvestAmount, //*
            securities: getters.portfolioSecurities,
            synced: state.rawPortfolio.synced,
            firstSynced: state.rawPortfolio.firstSynced,
            transfers: []
        };
        if (rootGetters[settingGetters.rebalancing]) {
            portfolioData = {
                ...portfolioData,
                rebalance: rootGetters[settingGetters.rebalancing]
            };
        }

        if (rootGetters[settingGetters.fee]) {
            portfolioData = {
                ...portfolioData,
                feeSetting: rootGetters[settingGetters.fee]
            };
        }

        if (rootGetters["taxes/taxSetting"]) {
            portfolioData = {
                ...portfolioData,
                taxSetting: rootGetters["taxes/taxSetting"]
            };
        }

        if (rootGetters["benchmark/benchmarkSetting"]) {
            portfolioData = {
                ...portfolioData,
                benchmark: rootGetters["benchmark/benchmarkSetting"]
            };
        }

        if (rootGetters["reinvestment/reinvestmentSetting"]) {
            portfolioData = {
                ...portfolioData,
                reinvestSetting: rootGetters["reinvestment/reinvestmentSetting"]
            };
        }

        if (rootGetters["transaction/subSequentSetting"]) {
            portfolioData = {
                ...portfolioData,
                subSequentSetting: rootGetters["transaction/subSequentSetting"]
            };
        }
        return portfolioData;
    },
    hasDataChange(state, getters, rootState, rootGetters) {
        return !isEqual(getters.portfolioData, state.rawPortfolio);
    },
    isEarliestAvailable(state) {
        return state.isApplyEarliestAvailable;
    },
    allocateByList() {
        return [
            {
                id: 0,
                labelId: "settings_allocateBy_amount"
            },
            {
                id: 1,
                labelId: "settings_allocateBy_percentage"
            },
            {
                id: 2,
                labelId: "settings_allocateBy_shares"
            }
        ];
    },
    broadAssetAllocationGroup(state) {
        const broadAssetAllocationGroup = state.assetAllocation.filter(i => i.groupType === 0);
        if (broadAssetAllocationGroup && broadAssetAllocationGroup.length > 0) {
            return broadAssetAllocationGroup;
        }
        return undefined;
    },
    presetTimePeriod(state) {
        const { adminConfig } = state.preferences;
        return adminConfig && adminConfig.localization && adminConfig.localization.presetTimePeriod;
    }
};

export default {
    state,
    mutations,
    getters,
    actions
};
